import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title
    document.title = 'Contact Revivacorp - Boca Raton Business Turnaround Consulting';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Contact Revivacorp for business turnaround and restructuring consulting in Boca Raton. Schedule a confidential consultation to discuss your business challenges and recovery solutions.');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/contact`);
    
    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Contact Revivacorp - Boca Raton Business Turnaround Consulting' },
      { property: 'og:description', content: 'Contact Revivacorp for business turnaround and restructuring consulting in Boca Raton. Schedule a confidential consultation to discuss your business challenges and recovery solutions.' },
      { property: 'og:url', content: `${siteUrl}/contact` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: `${siteUrl}/og-image.jpg` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    // Set Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Contact Revivacorp - Boca Raton Business Turnaround Consulting' },
      { name: 'twitter:description', content: 'Contact Revivacorp for business turnaround and restructuring consulting in Boca Raton. Schedule a confidential consultation to discuss your business challenges and recovery solutions.' },
      { name: 'twitter:image', content: `${siteUrl}/og-image.jpg` }
    ];
    
    twitterTags.forEach(tag => {
      let element = document.querySelector(`meta[name="${tag.name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', tag.name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
    
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Revivacorp',
      description: 'Business turnaround and restructuring consulting firm in Boca Raton',
      url: siteUrl,
      telephone: '+1-561-123-4567',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2255 Glades Rd',
        addressLocality: 'Boca Raton',
        addressRegion: 'FL',
        postalCode: '33431',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 26.3683,
        longitude: -80.1289
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '17:00'
        }
      ]
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Contact',
          item: `${siteUrl}/contact`
        }
      ]
    };

    const businessScript = document.createElement('script');
    businessScript.type = 'application/ld+json';
    businessScript.text = JSON.stringify(localBusinessSchema);
    document.head.appendChild(businessScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(businessScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([key, value]) => {
      data.append(key, value as string);
    });

    try {
      await fetch('https://readdy.ai/api/form/d6jinq2paf6ievb71gj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero */}
      <section className="relative h-72 flex items-end pb-12 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/e984ce96b0204a80e8a9a76a75719232.jpg"
          alt="Contact Revivacorp"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <p className="text-teal-400 text-sm font-semibold uppercase tracking-widest mb-2">Get In Touch</p>
          <h1 className="text-4xl font-bold text-white">Contact Revivacorp — Business Turnaround & Restructuring Consulting in Boca Raton</h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-teal-500 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-slate-700 font-medium">Contact Us</span>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* Left — Info */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Let's Start a Conversation</h2>
              <p className="text-slate-500 leading-relaxed mb-10">
                Whether your business is facing a cash flow crisis, operational challenges, or you simply need a trusted advisor — our team is ready to help. Reach out today for a confidential, no-obligation consultation.
              </p>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-0.5">Office Location</p>
                    <p className="text-sm text-slate-500">2255 Glades Rd<br />Boca Raton, FL 33431</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-0.5">Email</p>
                    <a href="mailto:info@revivacorp.com" className="text-sm text-slate-500 hover:text-teal-500 transition-colors">info@revivacorp.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-0.5">Business Hours</p>
                    <p className="text-sm text-slate-500">Monday – Friday: 9:00 AM – 6:00 PM<br />Saturday: 10:00 AM – 2:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden h-56 w-full border border-slate-100 shadow-sm">
                <iframe
                  title="Revivacorp Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3572.3!2d-80.1!3d26.36!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d8e7b0c0000001%3A0x1!2s2255+Glades+Rd%2C+Boca+Raton%2C+FL+33431!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>

            {/* Right — Form */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 lg:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 text-green-500 mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                  <p className="text-slate-500 text-sm max-w-xs">Thank you for reaching out. A member of our team will be in touch with you shortly.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 px-6 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-md hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">Send Us a Message</h3>
                  <p className="text-sm text-slate-400 mb-8">We'll respond within one business day.</p>

                  <form
                    data-readdy-form
                    id="contact-us-form"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">First Name <span className="text-teal-500">*</span></label>
                        <input
                          type="text"
                          name="first_name"
                          required
                          placeholder="John"
                          className="w-full border border-slate-200 rounded-md px-4 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Last Name <span className="text-teal-500">*</span></label>
                        <input
                          type="text"
                          name="last_name"
                          required
                          placeholder="Doe"
                          className="w-full border border-slate-200 rounded-md px-4 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email Address <span className="text-teal-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@company.com"
                        className="w-full border border-slate-200 rounded-md px-4 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Company Name</label>
                      <input
                        type="text"
                        name="company"
                        placeholder="Your Company Ltd."
                        className="w-full border border-slate-200 rounded-md px-4 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Service of Interest</label>
                      <select
                        name="service"
                        className="w-full border border-slate-200 rounded-md px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition cursor-pointer"
                      >
                        <option value="">Select a service...</option>
                        <option value="Cash Flow Coach">Cash Flow Coach</option>
                        <option value="Business Turnaround">Business Turnaround</option>
                        <option value="Fiduciary Services">Fiduciary Services</option>
                        <option value="Litigation Consulting">Litigation Consulting</option>
                        <option value="Capital Solutions">Capital Solutions</option>
                        <option value="CXO Services">CXO Services</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                        Message <span className="text-teal-500">*</span>
                        <span className={`ml-2 font-normal ${charCount > 500 ? 'text-red-500' : 'text-slate-400'}`}>({charCount}/500)</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="Tell us about your situation and how we can help..."
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full border border-slate-200 rounded-md px-4 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition resize-none"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || charCount > 500}
                      className="w-full py-3 bg-teal-500 text-white text-sm font-semibold rounded-md hover:bg-teal-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
